import { useMemo } from "react";
import { useTrendDisplay } from "../../../hooks/useTrendDisplay";
import { filterVisibleStands } from "../../../utils/visibleStands";
import type { MillVisualizerData } from "../models/MillVisualizer";
import { mapInFlightBillets } from "../utils/mapInFlightBillets";
import { mapToVisualizerData } from "../utils/mapToVisualizerData";

// Single source: MILL.GetTrendDisplayDataWeb returns 3 result sets:
//   #1 per-stand metrics (with PhysicalPosition in canonical 1..18 space),
//   #2 campaign banner,
//   #3 one row per in-flight billet with [FirstStandPosition, LastStandPosition]
//      already computed by the SP (no client-side cascade, no bucketing,
//      no inheritance, no bridging).
//
// The frontend just translates the SP's canonical positions to visible-array
// indices and packages the result for the SVG.
export const useMillVisualizerData = (): {
	data: MillVisualizerData;
	isLoading: boolean;
	error: unknown;
} => {
	const { data: trendData, isLoading, error } = useTrendDisplay();

	const visualizerData = useMemo(() => {
		const visible = filterVisibleStands(trendData?.stands ?? []);
		const billets = mapInFlightBillets(
			visible,
			trendData?.inFlightBillets ?? [],
		);
		return mapToVisualizerData(visible, billets, trendData?.banner ?? null);
	}, [trendData]);

	return { data: visualizerData, isLoading, error };
};
